import { RoleId } from '@wix/members-domain-ts';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import classNames from 'classnames';
import type { FC } from 'react';
import React from 'react';

import Button from '../../../../../../../common/components/Button';
import FollowButton from '../../../../../../../common/components/FollowButton';
import MemberMoreButton from '../../../../../../../common/components/MemberMoreButton';
import { EditProfileDetailsModal } from '../../../../../../../common/components/Modals';
import { ModalsContext } from '../../../../../../../contexts/modals';
import { useWidget } from '../../../../../../../contexts/widgets';
import { absorbArguments } from '../../../../../../../services/event-handler';
import { maybeOpenContactsWindow } from '../../../../../../../services/navigation';
import { DataHook, Position } from '../../../../../../../types';
import { CustomButton } from '../../common';
import styles from './Buttons.scss';

export const Buttons: FC = () => {
  const { t } = useTranslation();
  const { isRTL, isMobile } = useEnvironment();
  const { metaSiteId, member, site, profilePage, computed, handlers } =
    useWidget();

  const {
    inCommunity,
    showAsBlocked,
    canEdit,
    isDesignPreview,
    showFollowButton,
    showEditProfileDetailsButton,
    allowChat,
    profileWidgetTexts,
    rolesActions,
    additionalActions,
    isResponsiveEditor,
  } = computed;
  const { uid } = member;
  const { isProfilePreview } = profilePage;
  const { isSocial } = site;
  const { toggleIsEditingProfile, followOrUnfollow } = handlers;

  const isInDesignPreviewAndSocial = isDesignPreview && isSocial;

  const shouldShowChatButton = allowChat || isInDesignPreviewAndSocial;

  const handleChatButtonClick = absorbArguments(handlers.openChat);
  const handleFollowButtonClick = absorbArguments(followOrUnfollow);

  const moreActionsAlignment = Position.Right;

  const showMoreActions =
    rolesActions.length > 0 || additionalActions.length > 0;

  const handleRoleAction = (roleId: RoleId) => {
    if (roleId === RoleId.CONTACT_PAGE) {
      return maybeOpenContactsWindow(metaSiteId, uid, isResponsiveEditor);
    }

    handlers.executeRoleAction(roleId);
  };

  if (canEdit && !isProfilePreview && !isDesignPreview) {
    const { showJoinCommunityCTA, showEditProfileCTA } = computed;
    const isInSocialCommunity = inCommunity && isSocial;
    const handleEditButtonClick = absorbArguments(toggleIsEditingProfile);
    const handleCommunityButtonClick = () => {
      handlers.executeRoleAction(RoleId.JOIN_COMMUNITY);
    };

    return (
      <div className={styles.buttonsBox}>
        {showJoinCommunityCTA && (
          <div
            data-hook={DataHook.JoinCommunityCTA}
            className={styles.mainButton}
          >
            <CustomButton
              className={styles.truncateText}
              title={t('MemberRoles.action_set.community')}
              isSecondary={true}
              onClick={handleCommunityButtonClick}
            />
          </div>
        )}
        {isInSocialCommunity && showEditProfileCTA && (
          <div className={styles.mainButton}>
            <CustomButton
              className={classNames(
                styles.truncateText,
                styles.editProfileButton,
              )}
              dataHook={DataHook.EditProfileCTA}
              title={t('profile-widget.my-profile')}
              isSecondary={true}
              onClick={handleEditButtonClick}
            />
          </div>
        )}
        {showEditProfileDetailsButton && (
          <ModalsContext.Consumer>
            {(modalsContext) => (
              <div className={styles.mainButton}>
                <CustomButton
                  dataHook={DataHook.EditProfileDetailsCTA}
                  title={t('profile-widget.edit-details')}
                  isSecondary={true}
                  className={classNames(
                    styles.truncateText,
                    styles.editProfileButton,
                  )}
                  onClick={() => {
                    modalsContext?.showModal(EditProfileDetailsModal);
                  }}
                />
              </div>
            )}
          </ModalsContext.Consumer>
        )}
        {showMoreActions && (
          <MemberMoreButton
            containerClass={styles.moreActions}
            additionalActions={additionalActions}
            rolesActions={rolesActions}
            alignment={moreActionsAlignment}
            isMobile={isMobile}
            isRtlLocale={isRTL}
            isDisabled={isProfilePreview}
            onAdditionalAction={handlers.executeAdditionalAction}
            onRoleAction={handleRoleAction}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.buttonsBox}>
      {!showAsBlocked && (
        <>
          <div className={styles.mainButton}>
            {shouldShowChatButton && (
              <Button
                className={classNames(styles.commonBtn, styles.buttonSecondary)}
                isDisabled={profilePage.isProfilePreview}
                onClick={handleChatButtonClick}
              >
                {profileWidgetTexts.messageButtonText}
              </Button>
            )}
          </div>
          {showFollowButton && (
            <div className={classNames(styles.mainButton, styles.followButton)}>
              <FollowButton
                dataHook={DataHook.FollowCTA}
                className={styles.buttonSecondary}
                followText={profileWidgetTexts.followButtonText}
                unfollowText={profileWidgetTexts.followingButtonText}
                type="text"
                isFollowing={member.isSubscribed}
                isDisabled={profilePage.isProfilePreview}
                onClick={handleFollowButtonClick}
              />
            </div>
          )}
        </>
      )}
      {showMoreActions && (
        <MemberMoreButton
          containerClass={styles.moreActions}
          additionalActions={additionalActions}
          rolesActions={rolesActions}
          alignment={moreActionsAlignment}
          isMobile={isMobile}
          isRtlLocale={isRTL}
          isDisabled={isProfilePreview}
          onAdditionalAction={handlers.executeAdditionalAction}
          onRoleAction={handleRoleAction}
        />
      )}
    </div>
  );
};
